/**
 * SupplierCreditForm — single modal/slide-over for both Credit Memo (005)
 * and Subsequent Credit (006) flows.
 *
 *   - Credit Memo (short supply / qty rejected): qty-based lines
 *   - Subsequent Credit (price overbilled): qty=0, amount-only lines
 *
 * The variance-analysis endpoint prefills a reasonable set of lines; the
 * user approves/edits, then submits. The POST is Fire-and-Forget: we return
 * instantly with status=sap_submitting and poll until a terminal state.
 */
import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, fmtCurrency } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader2, AlertCircle, CheckCircle2, FileMinus, Scale } from "lucide-react";
import { toast } from "sonner";

const REASON_CODES_SHORT = [
  { value: "SHORT_SUPPLY",      label: "Short Supply" },
  { value: "QUALITY_REJECTION", label: "Quality Rejection" },
  { value: "OTHER",             label: "Other" },
];
const REASON_CODES_PRICE = [
  { value: "PRICE_OVERBILLED",  label: "Price Overbilled" },
  { value: "DISCOUNT_MISSED",   label: "Discount Missed" },
  { value: "OTHER",             label: "Other" },
];

export default function SupplierCreditForm({ open, onOpenChange, parentInvoice }) {
  const [flow, setFlow] = useState("credit_memo");
  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [lines, setLines] = useState([]);
  const [reasonCode, setReasonCode] = useState("SHORT_SUPPLY");
  const [reasonDetail, setReasonDetail] = useState("");
  const [supplierNoteNumber, setSupplierNoteNumber] = useState("");
  const [docDate, setDocDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [taxAmount, setTaxAmount] = useState(0);
  const [result, setResult] = useState(null);

  // Reset state every time the drawer opens with a (possibly different) parent.
  useEffect(() => {
    if (!open || !parentInvoice?.invoice_id) return;
    setResult(null);
    setError("");
    setFlow("credit_memo");
    setReasonCode("SHORT_SUPPLY");
    setReasonDetail("");
    setSupplierNoteNumber("");
    setDocDate(new Date().toISOString().slice(0, 10));
    setLoading(true);
    apiGet(`/invoices/${parentInvoice.invoice_id}/variance-analysis`)
      .then((v) => {
        setVariance(v);
        const taxRate = Number(v.effective_tax_rate_pct || 18); // default to 18% IGST if parent didn't carry it
        // Prefill lines for the current flow — fully populated so the user
        // doesn't retype anything the system already knows.
        const nextLines = (v.short_supply?.lines || []).map((l, i) => ({
          po_number: l.po_number,
          po_item_number: l.po_item_number,
          grn_number: l.grn_number,
          grn_item_number: l.grn_item_number,
          reference_invoice_item_id: String(i + 1),
          material_code: l.material_code,
          material_description: l.material_description,
          uom: l.uom,
          quantity: l.suggested_credit_qty,
          unit_price: l.unit_price,
          line_amount: l.suggested_amount,
          tax_code: l.tax_code || "83",
          hsn_sac_code: l.hsn_sac_code || "",
          tax_rate_pct: taxRate,
          reason: "Short supply",
          kind: "material",
        }));
        setLines(nextLines);
        // Auto-compute tax from each line's rate so Finance doesn't have to
        // key the GST reversal manually.
        const autoTax = nextLines.reduce(
          (sum, l) => sum + Number(l.line_amount || 0) * (Number(l.tax_rate_pct || 0) / 100),
          0,
        );
        setTaxAmount(Number(autoTax.toFixed(2)) || Number(v.short_supply?.suggested_tax_reversal || 0));
      })
      .catch((e) => setError(e.message || "Variance analysis failed"))
      .finally(() => setLoading(false));
  }, [open, parentInvoice?.invoice_id]);

  // When the user switches flow, reprefill lines from the matching variance bucket.
  useEffect(() => {
    if (!variance) return;
    const taxRate = Number(variance.effective_tax_rate_pct || 18);
    if (flow === "credit_memo") {
      setReasonCode("SHORT_SUPPLY");
      const nextLines = (variance.short_supply?.lines || []).map((l, i) => ({
        po_number: l.po_number,
        po_item_number: l.po_item_number,
        grn_number: l.grn_number,
        grn_item_number: l.grn_item_number,
        reference_invoice_item_id: String(i + 1),
        material_code: l.material_code,
        material_description: l.material_description,
        uom: l.uom,
        quantity: l.suggested_credit_qty,
        unit_price: l.unit_price,
        line_amount: l.suggested_amount,
        tax_code: l.tax_code || "83",
        hsn_sac_code: l.hsn_sac_code || "",
        tax_rate_pct: taxRate,
        reason: "Short supply",
        kind: "material",
      }));
      setLines(nextLines);
      const autoTax = nextLines.reduce(
        (sum, l) => sum + Number(l.line_amount || 0) * (Number(l.tax_rate_pct || 0) / 100),
        0,
      );
      setTaxAmount(Number(autoTax.toFixed(2)) || Number(variance.short_supply?.suggested_tax_reversal || 0));
    } else {
      setReasonCode("PRICE_OVERBILLED");
      setLines((variance.price_overbilled?.lines || []).map((l, i) => ({
        po_number: l.po_number,
        po_item_number: l.po_item_number,
        reference_invoice_item_id: String(i + 1),
        material_code: l.material_code,
        material_description: l.material_description,
        uom: l.uom,
        quantity: 0,
        unit_price: 0,
        line_amount: l.suggested_amount,
        tax_code: l.tax_code || "83",
        hsn_sac_code: l.hsn_sac_code || "",
        tax_rate_pct: taxRate,
        reason: `Price overbilled (${l.price_delta} per ${l.uom})`,
        kind: "material",
      })));
      setTaxAmount(0); // SAP computes tax on the subsequent-credit base automatically
    }
  }, [flow, variance]);

  // Add a non-material adjustment line (freight / penalty / rebate). HSN auto-copies
  // from the first material line — the user asked for exactly this behaviour so
  // SAP's GST reporting carries a consistent code across the credit.
  const addAdjustmentLine = (kind) => {
    const firstMaterial = lines.find((l) => l.kind === "material");
    const taxRate = Number(variance?.effective_tax_rate_pct || 18);
    const sharedHsn = firstMaterial?.hsn_sac_code || "";
    const defaults = {
      freight: { reason: "Freight reversal", description: "Freight credit" },
      penalty: { reason: "Supplier penalty", description: "Penalty / damages" },
      other: { reason: "Other adjustment", description: "Adjustment line" },
    }[kind] || { reason: "Other adjustment", description: "Adjustment line" };
    setLines((prev) => [
      ...prev,
      {
        po_number: null,
        po_item_number: null,
        grn_number: null,
        grn_item_number: null,
        reference_invoice_item_id: null,
        material_code: "",
        material_description: defaults.description,
        uom: "EA",
        quantity: 1,
        unit_price: 0,
        line_amount: 0,
        tax_code: "83",
        hsn_sac_code: sharedHsn,
        tax_rate_pct: taxRate,
        reason: defaults.reason,
        kind,
      },
    ]);
  };

  // When a line amount changes, auto-recompute the credit's total tax from
  // every line's own rate — keeps header balance=0 so SAP posts cleanly.
  useEffect(() => {
    const autoTax = lines.reduce(
      (sum, l) => sum + Number(l.line_amount || 0) * (Number(l.tax_rate_pct || 0) / 100),
      0,
    );
    setTaxAmount(Number(autoTax.toFixed(2)));
  }, [lines]);

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
  };
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const subtotal = lines.reduce((sum, l) => sum + Number(l.line_amount || 0), 0);
  const grandTotal = subtotal + Number(taxAmount || 0);

  const pollUntilTerminal = async (creditId) => {
    const deadline = Date.now() + 120000; // 2 min
    const terminal = new Set(["invoice_posted", "sap_draft_pending_review", "sap_posting_reconciliation_required", "sap_error"]);
    while (Date.now() < deadline) {
      await new Promise((r) => window.setTimeout(r, 1500));
      try {
        const latest = await apiGet(`/supplier-credits/${creditId}`);
        if (terminal.has(latest.status)) {
          return latest;
        }
      } catch (_) { /* keep polling */ }
    }
    return null;
  };

  const submit = useCallback(async () => {
    if (!parentInvoice?.invoice_id) return;
    if (lines.length === 0) {
      toast.error("Add at least one line");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        flow,
        reason_code: reasonCode,
        reason_detail: reasonDetail,
        document_date: docDate,
        lines,
        total_tax_amount: Number(taxAmount || 0),
        supplier_credit_note_number: supplierNoteNumber || null,
      };
      const created = await apiPost(`/invoices/${parentInvoice.invoice_id}/supplier-credit`, payload);
      toast.info(`Supplier credit queued (${created.credit_id}) — waiting for SAP…`);
      const terminal = (await pollUntilTerminal(created.credit_id)) || created;
      setResult(terminal);
      if (terminal.status === "invoice_posted") {
        const docId = terminal.sap_credit_id || terminal.sap_rts_id;
        toast.success(`SAP ${flow === "credit_memo" ? "Credit Memo" : "Subsequent Credit"} posted: ${docId}`);
      } else if (terminal.status === "sap_draft_pending_review") {
        const docId = terminal.sap_credit_id || terminal.sap_rts_id;
        toast.success(`SAP draft created: ${docId} — awaiting posting`);
      } else if (terminal.status === "sap_posting_reconciliation_required") {
        const docId = terminal.sap_credit_id || terminal.sap_rts_id;
        toast.warning(`SAP draft ${docId} created but needs review in ByD: ${terminal.sap_error_message || "exceptions remain"}`);
      } else if (terminal.status === "sap_error") {
        toast.error(terminal.sap_error_message || "SAP rejected the request");
      } else {
        toast.info(`Still processing — status: ${terminal.status}`);
      }
    } catch (e) {
      setError(e.message || "Submission failed");
      toast.error(e.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }, [parentInvoice?.invoice_id, flow, reasonCode, reasonDetail, docDate, lines, taxAmount, supplierNoteNumber]);

  const reasonOptions = flow === "credit_memo" ? REASON_CODES_SHORT : REASON_CODES_PRICE;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!inset-0 !h-[100dvh] !w-full !max-w-none overflow-y-auto bg-white p-4 sm:!inset-y-0 sm:!left-auto sm:!right-0 sm:!h-full sm:!w-[640px] sm:!max-w-[640px] sm:p-6" data-testid="supplier-credit-form">
        <SheetHeader className="pb-4 border-b border-slate-100">
          <SheetTitle className="font-heading text-slate-900 text-[15px]">Raise Supplier Credit</SheetTitle>
          <SheetDescription className="font-mono text-[11px] text-slate-400">
            Against SAP {parentInvoice?.sap_invoice_id || "—"} · {parentInvoice?.vendor_name || parentInvoice?.vendor_id}{(parentInvoice?.plant_code || parentInvoice?.site) ? ` · Site ${parentInvoice.plant_code || parentInvoice.site}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="pt-4 space-y-5">
          {/* Flow toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFlow("credit_memo")}
              disabled={submitting}
              className={`rounded border p-3 text-left text-[12px] transition-colors ${flow === "credit_memo" ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-slate-200 bg-white"}`}
              data-testid="flow-credit-memo"
            >
              <div className="flex items-center gap-2 font-semibold text-slate-900">
                <FileMinus size={13} /> Credit Memo (005 · qty > 0)
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Short supply / rejected qty · reverses GR/IR</p>
            </button>
            <button
              type="button"
              onClick={() => setFlow("subsequent_credit")}
              disabled={submitting}
              className={`rounded border p-3 text-left text-[12px] transition-colors ${flow === "subsequent_credit" ? "border-amber-600 bg-amber-50 ring-1 ring-amber-600" : "border-slate-200 bg-white"}`}
              data-testid="flow-subsequent-credit"
            >
              <div className="flex items-center gap-2 font-semibold text-slate-900">
                <Scale size={13} /> Subsequent Credit (005 · qty 0)
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Price overbilled · value-only, no qty reversal</p>
            </button>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-[13px] text-slate-500" data-testid="variance-loading">
              <Loader2 size={14} className="animate-spin" /> Analysing variance against GRN and PO prices…
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-[12px] text-red-700" data-testid="variance-error">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && variance && lines.length === 0 && (
            <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="variance-no-suggestions">
              <p className="text-[12px] text-slate-500 mb-3">
                No {flow === "credit_memo" ? "qty variance (short supply/rejection)" : "price variance (overbilling)"} detected on this invoice.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  // Seed one editable blank line so the user can drive a test
                  // (particularly useful for Subsequent Credit 006 tenant-config probing)
                  const firstPoLine = parentInvoice?.line_items?.[0];
                  setLines([{
                    po_number: firstPoLine?.po_number || "",
                    po_item_number: firstPoLine?.po_item_number || "1",
                    reference_invoice_item_id: "1",
                    material_code: firstPoLine?.material_code || "",
                    material_description: firstPoLine?.material_description || "Manual credit line",
                    uom: firstPoLine?.uom || "EA",
                    quantity: flow === "credit_memo" ? 1 : 0,
                    unit_price: flow === "credit_memo" ? Number(firstPoLine?.unit_price || 0) : 0,
                    line_amount: flow === "credit_memo" ? Number(firstPoLine?.unit_price || 0) : 1,
                    tax_code: firstPoLine?.tax_code || "",
                    reason: flow === "credit_memo" ? "Short supply (manual)" : "Price overbilled (manual)",
                  }]);
                }}
                data-testid="add-manual-credit-line"
              >
                Add manual line (for test / supplier credit note)
              </Button>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Reason code</Label>
              <select
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                disabled={submitting}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-2 text-[13px]"
                data-testid="credit-reason-code"
              >
                {reasonOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Document date</Label>
              <DatePickerInput
                value={docDate}
                onChange={setDocDate}
                disabled={submitting}
                className="mt-1"
                data-testid="credit-doc-date"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase text-slate-500">Supplier's credit-note number (for audit)</Label>
              <Input
                value={supplierNoteNumber}
                onChange={(e) => setSupplierNoteNumber(e.target.value)}
                placeholder="e.g. CN-2026-001"
                disabled={submitting}
                className="mt-1"
                data-testid="supplier-credit-note-number"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase text-slate-500">Reason detail (optional)</Label>
              <Input
                value={reasonDetail}
                onChange={(e) => setReasonDetail(e.target.value)}
                placeholder="Short notes for audit"
                disabled={submitting}
                className="mt-1"
                data-testid="credit-reason-detail"
              />
            </div>
          </div>

          {/* Line editor */}
          {lines.length > 0 && (
            <div className="border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-200 border-b-2 border-slate-300">
                  <tr>
                    <th className="px-2 py-2.5 text-left font-bold text-slate-700 uppercase text-[11px]">Material</th>
                    <th className="px-2 py-2.5 text-left font-bold text-slate-700 uppercase text-[11px]">PO</th>
                    <th className="px-2 py-2.5 text-left font-bold text-slate-700 uppercase text-[11px]">HSN</th>
                    {flow === "credit_memo" && <th className="px-2 py-2.5 text-right font-bold text-slate-700 uppercase text-[11px]">Qty</th>}
                    {flow === "credit_memo" && <th className="px-2 py-2.5 text-right font-bold text-slate-700 uppercase text-[11px]">Unit ₹</th>}
                    <th className="px-2 py-2.5 text-right font-bold text-slate-700 uppercase text-[11px]">Amount ₹</th>
                    <th className="px-2 py-2.5 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300">
                  {lines.map((line, idx) => (
                    <tr key={idx} data-testid={`credit-line-${idx}`}>
                      <td className="px-2 py-2 text-slate-800 truncate max-w-[160px]" title={line.material_description}>
                        <div className="font-medium">
                          {line.material_code || <span className="italic text-slate-400">({line.kind})</span>}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">{line.material_description}</div>
                      </td>
                      <td className="px-2 py-2 font-mono text-[11px] text-slate-600">
                        {line.po_number ? `${line.po_number}·${line.po_item_number}` : "—"}
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          value={line.hsn_sac_code || ""}
                          onChange={(e) => updateLine(idx, { hsn_sac_code: e.target.value })}
                          disabled={submitting}
                          placeholder="HSN/SAC"
                          className="w-24 text-left font-mono text-[11px]"
                          data-testid={`credit-line-hsn-${idx}`}
                        />
                      </td>
                      {flow === "credit_memo" && (
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.001"
                            value={line.quantity}
                            onChange={(e) => {
                              const q = Number(e.target.value) || 0;
                              updateLine(idx, { quantity: q, line_amount: Number((q * Number(line.unit_price || 0)).toFixed(2)) });
                            }}
                            disabled={submitting}
                            className="w-20 text-right"
                            data-testid={`credit-line-qty-${idx}`}
                          />
                        </td>
                      )}
                      {flow === "credit_memo" && (
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unit_price}
                            onChange={(e) => {
                              const p = Number(e.target.value) || 0;
                              updateLine(idx, { unit_price: p, line_amount: Number((Number(line.quantity || 0) * p).toFixed(2)) });
                            }}
                            disabled={submitting}
                            className="w-24 text-right"
                            data-testid={`credit-line-price-${idx}`}
                          />
                        </td>
                      )}
                      <td className="px-2 py-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.line_amount}
                          onChange={(e) => updateLine(idx, { line_amount: Number(e.target.value) || 0 })}
                          disabled={submitting}
                          className="w-28 text-right font-medium"
                          data-testid={`credit-line-amount-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeLine(idx)}
                          disabled={submitting}
                          className="text-[11px] text-red-600 hover:text-red-800"
                          data-testid={`credit-line-remove-${idx}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add non-material adjustment lines (freight / penalty / rebate).
              HSN auto-copies from the first material line so SAP's GST
              reporting carries one consistent code across the credit. */}
          {lines.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" disabled={submitting}
                onClick={() => addAdjustmentLine("freight")} data-testid="add-freight-line">
                + Freight line
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={submitting}
                onClick={() => addAdjustmentLine("penalty")} data-testid="add-penalty-line">
                + Penalty / damages line
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={submitting}
                onClick={() => addAdjustmentLine("other")} data-testid="add-other-line">
                + Other adjustment
              </Button>
            </div>
          )}

          {/* Totals */}
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[12px]">
            <div className="flex justify-between py-0.5">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-mono font-medium" data-testid="credit-subtotal">{fmtCurrency(subtotal, parentInvoice?.currency || "INR")}</span>
            </div>
            {flow === "credit_memo" && (
              <div className="flex justify-between items-center py-0.5">
                <span className="text-slate-600">GST reversal</span>
                <Input
                  type="number"
                  step="0.01"
                  value={taxAmount}
                  onChange={(e) => setTaxAmount(Number(e.target.value) || 0)}
                  disabled={submitting}
                  className="w-28 text-right"
                  data-testid="credit-tax-amount"
                />
              </div>
            )}
            {flow === "subsequent_credit" && (
              <p className="py-1 text-[11px] text-amber-700">Subsequent Credit: SAP computes GST on the delta base automatically — no override here.</p>
            )}
            <div className="flex justify-between py-1 mt-1 border-t border-slate-200 pt-2">
              <span className="text-slate-800 font-semibold">Grand Total</span>
              <span className="font-mono font-semibold text-slate-900" data-testid="credit-grand-total">{fmtCurrency(grandTotal, parentInvoice?.currency || "INR")}</span>
            </div>
          </div>

          {/* Predicted GL postings — mirrors SAP ByD journal entry layout
              so finance can sanity-check the accounting impact BEFORE submitting.
              Logic derived from the user's live SAP test (RTDN-3-2026, Credit Memo
              journal entry 2200000000003): Credit Memo credits GRNI; Subsequent
              Credit credits Price Difference (or MAP on remaining stock). */}
          {lines.length > 0 && (
            <div className="rounded border border-slate-200 overflow-hidden" data-testid="credit-gl-preview">
              <header className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between">
                <p className="text-[11px] font-mono uppercase text-slate-500 tracking-wider">Predicted SAP GL Postings (preview)</p>
                <span className="text-[10px] text-slate-400">Mode: Simulation · not posted</span>
              </header>
              <table className="w-full text-[12px]">
                <thead className="bg-slate-200 border-b-2 border-slate-300 text-left">
                  <tr>
                    <th className="px-3 py-2 font-bold text-slate-700 uppercase text-[11px]">G/L Account</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-700 uppercase text-[11px]">Debit (INR)</th>
                    <th className="px-3 py-2 text-right font-bold text-slate-700 uppercase text-[11px]">Credit (INR)</th>
                    <th className="px-3 py-2 font-bold text-slate-700 uppercase text-[11px]">Why</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300">
                  <tr data-testid="gl-row-ap">
                    <td className="px-3 py-1.5 font-mono">CR-RAW-D · Creditors</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtCurrency(grandTotal, parentInvoice?.currency || "INR").replace(/^[^\d-]+/, "")}</td>
                    <td className="px-3 py-1.5 text-right text-slate-300 font-mono">—</td>
                    <td className="px-3 py-1.5 text-slate-600 text-[11px]">Reduces payable to supplier</td>
                  </tr>
                  {Number(taxAmount || 0) > 0 && (
                    <tr data-testid="gl-row-tax">
                      <td className="px-3 py-1.5 font-mono">IGST / CGST / SGST INPUT</td>
                      <td className="px-3 py-1.5 text-right text-slate-300 font-mono">—</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtCurrency(Number(taxAmount || 0), parentInvoice?.currency || "INR").replace(/^[^\d-]+/, "")}</td>
                      <td className="px-3 py-1.5 text-slate-600 text-[11px]">Reverses input tax credit on the credited portion</td>
                    </tr>
                  )}
                  {flow === "credit_memo" ? (
                    <tr data-testid="gl-row-grni">
                      <td className="px-3 py-1.5 font-mono text-emerald-800">GRNI · Goods Received Not Invoiced</td>
                      <td className="px-3 py-1.5 text-right text-slate-300 font-mono">—</td>
                      <td className="px-3 py-1.5 text-right font-mono text-emerald-800 font-semibold">{fmtCurrency(subtotal, parentInvoice?.currency || "INR").replace(/^[^\d-]+/, "")}</td>
                      <td className="px-3 py-1.5 text-emerald-700 text-[11px]">Knocks off the dangling short-qty GRNI debit → net ZERO (no inventory valuation impact)</td>
                    </tr>
                  ) : (
                    <tr data-testid="gl-row-pricediff">
                      <td className="px-3 py-1.5 font-mono text-amber-800">PRICE-DIFF · Price Difference / Inventory</td>
                      <td className="px-3 py-1.5 text-right text-slate-300 font-mono">—</td>
                      <td className="px-3 py-1.5 text-right font-mono text-amber-800 font-semibold">{fmtCurrency(subtotal, parentInvoice?.currency || "INR").replace(/^[^\d-]+/, "")}</td>
                      <td className="px-3 py-1.5 text-amber-700 text-[11px]">
                        If stock consumed → Price Difference. If stock remains → adjusts MAP on remaining qty only. NO qty reversal.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <td className="px-3 py-1.5 text-[11px] font-mono text-slate-500">TOTAL</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtCurrency(grandTotal, parentInvoice?.currency || "INR").replace(/^[^\d-]+/, "")}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtCurrency(subtotal + Number(taxAmount || 0), parentInvoice?.currency || "INR").replace(/^[^\d-]+/, "")}</td>
                    <td className="px-3 py-1.5 text-[11px] text-slate-500">Balanced ✓</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className={`flex items-start gap-2 rounded border p-3 text-[12px] ${result.status === "sap_draft_pending_review" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-red-200 bg-red-50 text-red-700"}`}
              data-testid="credit-result"
            >
              {result.status === "sap_draft_pending_review"
                ? <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                : <AlertCircle size={13} className="mt-0.5 shrink-0" />
              }
              <div>
                {result.status === "sap_draft_pending_review" && (
                  result.flow === "credit_memo" && result.sap_rts_id ? (
                    <>
                      <p><strong>SAP Return-to-Supplier draft created:</strong> {result.sap_rts_id}</p>
                      <p className="mt-1 text-[11px] text-emerald-800">
                        SAP ByD will generate the Credit Memo supplier-invoice automatically once Finance releases this RTS.
                      </p>
                    </>
                  ) : (
                    <p><strong>SAP {result.flow === "credit_memo" ? "Credit Memo" : "Subsequent Credit"} created:</strong> {result.sap_credit_id || result.sap_rts_id}</p>
                  )
                )}
                {result.status === "sap_error" && (
                  <p><strong>SAP rejected the request.</strong> {result.sap_error_message}</p>
                )}
                {!["sap_draft_pending_review", "sap_error"].includes(result.status) && (
                  <p>Status: {result.status} — poll again in Credit Notes page.</p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="credit-cancel"
            >
              Close
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || loading || lines.length === 0 || !!(result?.sap_credit_id || result?.sap_rts_id)}
              className="bg-[#0A6755] hover:bg-[#065043] text-white gap-2"
              data-testid="credit-submit"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : null}
              {submitting ? "Submitting to SAP…" : `Raise ${flow === "credit_memo" ? "Credit Memo" : "Subsequent Credit"}`}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
